// per docs/ui-shape-grafana-authoring.md §2/§5/§6, caiet §6 #7, UC26-01/02,
// UC61-03, UC-DASH-03/04/07/11.
//
// One SIMSM dashboard is one Grafana dashboard plus what Grafana cannot know:
// the SIMSM address, who reaches it, where it sits in the observer's menu, how
// often it refreshes and on which interval it opens. The panels themselves are
// authored in Grafana's own editor, so this form's first act is to send the
// administrator there and its second is to let them pick what they drew.
//
// ACCS-16, CF-18: the title, the description and the Grafana dashboard are the
// Romanian ones; the Translations section says them in Russian and English, and
// an empty field falls back to the Romanian one
// (docs/shape-ops-dashboard-localization.md §2.5).
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import {
  MudButton,
  MudCheckbox,
  MudIcon,
  MudTextInput,
  MudInfoBox,
  MudSelect,
  MudSwitch,
  MudTag,
} from '@egov-moldova/mud-react';
import {
  DASHBOARD_DEFAULT_RANGES,
  DASHBOARD_RANGE_LIMITS,
  DASHBOARD_TRANSLATION_LOCALES,
  defaultRangeDays,
  isObserverRole,
  rangeLimitDays,
  type DashboardTranslation,
  type DashboardTranslationLocale,
} from '@simsm/shared-types';
import { LOCALE_LABELS } from '@simsm/ui-i18n';
import {
  type ApiRefusal,
  type LoadFailureKind,
  apiClient,
  apiRefusal,
  loadFailureKind,
} from '../../app/api-client';
import { FormField, FormPage, FormRow, FormSection } from '../../components/FormPage/FormPage';
import { useObserverRoles, useRoleLabel } from '../../hooks/use-observer-roles';
import { resolveDashboardReach, undefinedGrants } from '../../hooks/use-dashboard-reach';
import { useGrafanaDashboards, type GrafanaDashboardRef } from '../../hooks/use-grafana-dashboards';
import { GrafanaDashboardPicker } from './GrafanaDashboardPicker';
import './dashboards-admin.css';

interface DashboardView {
  id: string;
  slug: string;
  title: string;
  description: string;
  translations?: DashboardTranslation[];
  roleScope: string[];
  grafanaUid: string;
  refreshIntervalSeconds: number;
  defaultRange: string;
  maxRange: string;
  maxLookback: string;
  sortOrder: number;
  active: boolean;
  version: number;
  updatedAtIso: string;
}

type TranslationFields = Omit<DashboardTranslation, 'locale'>;

type TranslationsForm = Record<DashboardTranslationLocale, TranslationFields>;

interface FormState {
  slug: string;
  title: string;
  description: string;
  translations: TranslationsForm;
  refreshIntervalSeconds: number;
  defaultRange: string;
  maxRange: string;
  maxLookback: string;
  active: boolean;
  roleScope: Set<string>;
  grafanaUid: string;
}

const REFRESH_OPTIONS = [
  { value: '60', label: '1 min' },
  { value: '300', label: '5 min' },
  { value: '900', label: '15 min' },
  { value: '3600', label: '60 min' },
];

// UC-DASH-04. A default either limit would never let the viewer select is a
// contradiction; the analytics service refuses it too, this catches it inline.
// The vocabulary is the shared one (@simsm/shared-types dashboard-time-range.ts).
// `max_range` bounds the length of the interval, `max_lookback` how far before
// today it may start (docs/shape-ops-dashboard-time-range.md §2.1).
export function defaultRangeExceedsMax(defaultRange: string, maxRange: string): boolean {
  return defaultRangeDays(defaultRange) > rangeLimitDays(maxRange);
}

function translationsForm(stored: readonly DashboardTranslation[] = []): TranslationsForm {
  const form = {} as TranslationsForm;
  for (const locale of DASHBOARD_TRANSLATION_LOCALES) {
    const entry = stored.find((translation) => translation.locale === locale);
    form[locale] = {
      title: entry?.title ?? '',
      description: entry?.description ?? '',
      grafanaUid: entry?.grafanaUid ?? '',
    };
  }
  return form;
}

// A delivered dashboard's variants are `<uid>-ru` and `<uid>-en`
// (deploy/grafana/README.md), so picking the Romanian one fills in a language
// still empty when Grafana holds its variant. A uid already chosen stays.
export function prefillTranslationUids(
  translations: TranslationsForm,
  uid: string,
  catalogue: readonly GrafanaDashboardRef[],
): TranslationsForm {
  const next = { ...translations };
  for (const locale of DASHBOARD_TRANSLATION_LOCALES) {
    const variant = `${uid}-${locale}`;
    if (next[locale].grafanaUid === '' && catalogue.some((row) => row.uid === variant)) {
      next[locale] = { ...next[locale], grafanaUid: variant };
    }
  }
  return next;
}

// The role-first editor creates "a visualisation for THIS role", so it arrives
// with ?role=<CODE> and the sharing checkbox is already ticked. Seeding both the
// form and its baseline keeps the page undirty until the administrator types.
function emptyForm(presetRole?: string): FormState {
  return {
    slug: '',
    title: '',
    description: '',
    translations: translationsForm(),
    refreshIntervalSeconds: 300,
    defaultRange: 'last-30-days',
    maxRange: '24-months',
    maxLookback: '24-months',
    active: true,
    // A new dashboard starts shared with every observer, the grant OBSERVER, as
    // the wireframe's form does; an empty selection is "not shared", the System
    // Administrator only (docs/shape-ops-dashboard-sharing.md §2.9).
    roleScope: new Set(presetRole ? [presetRole] : ['OBSERVER']),
    grafanaUid: '',
  };
}

function dashboardToForm(d: DashboardView): FormState {
  return {
    slug: d.slug,
    title: d.title,
    description: d.description,
    translations: translationsForm(d.translations),
    refreshIntervalSeconds: d.refreshIntervalSeconds,
    defaultRange: d.defaultRange,
    maxRange: d.maxRange,
    maxLookback: d.maxLookback || d.maxRange,
    active: d.active,
    roleScope: new Set(d.roleScope),
    grafanaUid: d.grafanaUid,
  };
}

// Set is not JSON-serializable; expand it so dirty-checking sees roleScope changes.
function serializeForm(form: FormState): string {
  return JSON.stringify({ ...form, roleScope: Array.from(form.roleScope).sort() });
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function DashboardFormPage(): JSX.Element {
  const { t } = useTranslate();
  const roleLabel = useRoleLabel();
  const definedRoles = useObserverRoles();
  const grafanaCatalogue = useGrafanaDashboards();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(slug);
  const presetRole = isEdit ? '' : (searchParams.get('role') ?? '');

  const [form, setForm] = useState<FormState>(() => emptyForm(presetRole));
  const [initial, setInitial] = useState<FormState>(() => emptyForm(presetRole));
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [refusal, setRefusal] = useState<ApiRefusal | null>(null);
  const [newRoleCode, setNewRoleCode] = useState('OBSERVER_');
  const [newRoleName, setNewRoleName] = useState('');

  // Leaving the form returns where the administrator came from: the role-first
  // editor when it sent us here, the dashboard list otherwise.
  const returnTo = presetRole
    ? `/system-admin/dashboards-admin?section=by-role&role=${encodeURIComponent(presetRole)}`
    : '/system-admin/dashboards-admin';

  // No single-dashboard admin endpoint exists; reuse the admin list and pick by slug.
  const dashboards = useQuery({
    queryKey: ['admin-dashboards'],
    queryFn: () =>
      apiClient
        .get<{ dashboards: DashboardView[] }>('/admin/dashboards')
        .then((r) => r.data.dashboards),
  });
  const editing = isEdit ? (dashboards.data ?? []).find((d) => d.slug === slug) : undefined;
  const editingId = editing?.id ?? null;

  useEffect(() => {
    if (editing) {
      const next = dashboardToForm(editing);
      setForm(next);
      setInitial(next);
    }
  }, [editing]);

  // caiet UC26-02 shares a dashboard with role NAMES, and UC61-03 leaves the set
  // of names open, so the checkboxes come from the definition table. A scope
  // entry whose role has since been deactivated stays on the list: dropping it
  // from the form would revoke it the next time anyone saves.
  const roleCodes = useMemo(
    () => (definedRoles.data ?? []).map((role) => role.roleCode),
    [definedRoles.data],
  );
  const observerRoles = useMemo(
    () => [...roleCodes, ...undefinedGrants(Array.from(form.roleScope), roleCodes)],
    [roleCodes, form.roleScope],
  );

  // D5 — the consequence of the checkboxes above, spelled out: a grant to
  // OBSERVER already carries every Specific Observer, so an administrator who
  // cannot see that grants it twice.
  const scope = useMemo(() => Array.from(form.roleScope), [form.roleScope]);
  const reach = resolveDashboardReach(scope, roleCodes);

  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: ['admin-dashboards'] });
    queryClient.invalidateQueries({ queryKey: ['dashboards-list'] });
  };

  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      editingId
        ? apiClient.patch<DashboardView>(`/admin/dashboards/${editingId}`, payload)
        : apiClient.post<DashboardView>('/admin/dashboards', payload),
    onSuccess: () => {
      invalidate();
      navigate(returnTo);
    },
    onError: (e: unknown) => setRefusal(apiRefusal(e, 'dashboards_admin.error.save_failed')),
  });

  // UC61-03: the administrator mints the role in MPass, so the definition SIMSM
  // needs must be creatable at the moment of sharing rather than on a second
  // screen. The new role is ticked as soon as it exists.
  const createRole = useMutation({
    mutationFn: (payload: { roleCode: string; displayName: string }) =>
      apiClient
        .post<{ roleCode: string }>('/admin/observer-roles', {
          ...payload,
          description: '',
          medicineTags: [],
          registrarKinds: [],
          active: true,
        })
        .then((r) => r.data),
    onSuccess: (role) => {
      queryClient.invalidateQueries({ queryKey: ['observer-roles'] });
      queryClient.invalidateQueries({ queryKey: ['admin-observer-roles'] });
      setForm((current) => ({
        ...current,
        roleScope: new Set([...current.roleScope, role.roleCode]),
      }));
      setNewRoleCode('OBSERVER_');
      setNewRoleName('');
    },
    // Minting the role is its own operation, not the dashboard's save: a
    // refusal here must not be headlined as a failed save, and the code it
    // carries (OBSERVER_ROLE_EXISTS, OBSERVER_ROLE_CODE_INVALID) has copy of
    // its own.
    onError: (e: unknown) => setRefusal(apiRefusal(e, 'observer_roles.error.create_failed')),
  });

  const isDirty = serializeForm(form) !== serializeForm(initial);

  // The wireframe rejects a duplicate title and a taken address in the form
  // itself, on the field the administrator has to change. The server refuses a
  // taken slug too, and since C4 that refusal is legible — it answers 400
  // DASHBOARD_SLUG_TAKEN, which the shared table turns into Romanian copy — but
  // it lands in the page banner, not on the field. Both are kept: this check
  // marks the field, the server's is the backstop for a slug taken between the
  // check and the save.
  const others = (dashboards.data ?? []).filter((d) => d.id !== editingId);
  const slugInvalid =
    isEdit || /^[a-z0-9][a-z0-9-]{2,79}$/.test(form.slug.trim().toLowerCase())
      ? others.some((d) => d.slug === form.slug.trim().toLowerCase())
        ? t('dashboards_admin.form.error.slug_taken')
        : null
      : t('dashboards_admin.form.error.slug');
  const titleInvalid =
    form.title.trim().length === 0
      ? t('dashboards_admin.form.error.title')
      : others.some((d) => d.title.trim().toLowerCase() === form.title.trim().toLowerCase())
        ? t('dashboards_admin.form.error.title_taken')
        : null;
  const rangeInvalid = defaultRangeExceedsMax(form.defaultRange, form.maxRange)
    ? t('dashboards_admin.form.error.range')
    : null;
  const lookbackInvalid = defaultRangeExceedsMax(form.defaultRange, form.maxLookback)
    ? t('dashboards_admin.form.error.lookback')
    : null;
  // Grafana owns the panels, so a dashboard without one embeds nothing.
  const sourceInvalid =
    form.grafanaUid.trim().length === 0 ? t('dashboards_admin.form.error.source') : null;

  const newRoleCodeReady =
    isObserverRole(newRoleCode.trim().toUpperCase()) &&
    newRoleCode.trim().toUpperCase() !== 'OBSERVER' &&
    newRoleName.trim().length > 0 &&
    !observerRoles.includes(newRoleCode.trim().toUpperCase());

  function submit(): void {
    setFormSubmitted(true);
    if (slugInvalid || titleInvalid || rangeInvalid || lookbackInvalid || sourceInvalid) return;
    setRefusal(null);
    save.mutate({
      slug: form.slug.trim().toLowerCase(),
      title: form.title.trim(),
      description: form.description.trim(),
      translations: DASHBOARD_TRANSLATION_LOCALES.map((locale) => ({
        locale,
        title: form.translations[locale].title.trim(),
        description: form.translations[locale].description.trim(),
        grafanaUid: form.translations[locale].grafanaUid.trim(),
      })),
      roleScope: scope,
      grafanaUid: form.grafanaUid.trim(),
      refreshIntervalSeconds: form.refreshIntervalSeconds,
      defaultRange: form.defaultRange,
      maxRange: form.maxRange,
      maxLookback: form.maxLookback,
      active: form.active,
    });
  }

  function setTranslation(
    locale: DashboardTranslationLocale,
    fields: Partial<TranslationFields>,
  ): void {
    setForm((current) => ({
      ...current,
      translations: {
        ...current.translations,
        [locale]: { ...current.translations[locale], ...fields },
      },
    }));
  }

  // The Grafana dashboards a language may embed. A stored uid Grafana no longer
  // lists stays selectable under its uid, so the select still shows what is saved.
  function translationSourceOptions(selected: string): { value: string; label: string }[] {
    const rows = grafanaCatalogue.data ?? [];
    return [
      { value: '', label: t('dashboards_admin.form.translation.source_romanian') },
      ...(selected !== '' && !rows.some((row) => row.uid === selected)
        ? [{ value: selected, label: selected }]
        : []),
      ...rows.map((row) => ({ value: row.uid, label: `${row.title} · ${row.uid}` })),
    ];
  }

  function toggleRole(role: string, checked: boolean): void {
    const next = new Set(form.roleScope);
    if (checked) next.add(role);
    else next.delete(role);
    setForm({ ...form, roleScope: next });
  }

  // Absence is claimed from a *successful* list that does not contain this
  // dashboard, never from a failed one.
  const loadError: LoadFailureKind | null = !isEdit
    ? null
    : dashboards.isError
      ? loadFailureKind(dashboards.error)
      : dashboards.isSuccess && !editing
        ? 'not_found'
        : null;

  return (
    <FormPage
      breadcrumb={[
        { label: t('nav.dashboards_admin'), href: returnTo },
        {
          label: isEdit
            ? t('dashboards_admin.form.edit_title')
            : t('dashboards_admin.form.create_title'),
        },
      ]}
      title={
        isEdit ? t('dashboards_admin.form.edit_title') : t('dashboards_admin.form.create_title')
      }
      lede={t('dashboards_admin.form.lede')}
      saveLabel={
        isEdit
          ? t('dashboards_admin.form.save_as_version', { version: (editing?.version ?? 0) + 1 })
          : t('dashboards_admin.form.create_submit')
      }
      isSaving={save.isPending}
      isDirty={isDirty}
      isLoading={isEdit && dashboards.isLoading}
      loadError={loadError}
      refusal={refusal}
      canSave={!isEdit || Boolean(editingId)}
      onSave={submit}
      onCancel={() => navigate(returnTo)}
    >
      <FormSection>
        <fieldset className="simsm-dashboards-admin__fieldset">
          <legend>{t('dashboards_admin.form.source')}</legend>
          <p className="simsm-dashboards-admin__form-hint">
            {t('dashboards_admin.form.source_hint')}
          </p>
          <GrafanaDashboardPicker
            value={form.grafanaUid}
            invalid={formSubmitted ? (sourceInvalid ?? undefined) : undefined}
            onChange={(uid, title) =>
              setForm((current) => ({
                ...current,
                grafanaUid: uid,
                title: current.title.trim() ? current.title : title,
                slug: current.slug || (isEdit ? current.slug : slugify(title)),
                translations: prefillTranslationUids(
                  current.translations,
                  uid,
                  grafanaCatalogue.data ?? [],
                ),
              }))
            }
          />
        </fieldset>
      </FormSection>

      <FormSection>
        <FormRow>
          <FormField
            label={t('dashboards_admin.form.title')}
            required
            error={formSubmitted ? (titleInvalid ?? undefined) : undefined}
            hint={t('dashboards_admin.form.title_hint')}
          >
            <MudTextInput
              size="md"
              value={form.title}
              onMudInput={(e: CustomEvent<{ value: string }>) =>
                setForm({
                  ...form,
                  title: e.detail.value,
                  slug: isEdit ? form.slug : slugify(e.detail.value),
                })
              }
            />
          </FormField>
          <FormField
            label={t('dashboards_admin.form.slug')}
            required={!isEdit}
            error={formSubmitted ? (slugInvalid ?? undefined) : undefined}
            hint={t('dashboards_admin.form.slug_hint', { slug: form.slug || '…' })}
          >
            <MudTextInput
              size="md"
              value={form.slug}
              disabled={isEdit}
              placeholder="stoc-national"
              onMudInput={(e: CustomEvent<{ value: string }>) =>
                setForm({ ...form, slug: e.detail.value.toLowerCase() })
              }
            />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField label={t('dashboards_admin.form.description')}>
            <MudTextInput
              size="md"
              value={form.description}
              placeholder={t('dashboards_admin.form.description_placeholder')}
              onMudInput={(e: CustomEvent<{ value: string }>) =>
                setForm({ ...form, description: e.detail.value })
              }
            />
          </FormField>
          <FormField
            label={t('dashboards_admin.form.refresh')}
            required
            hint={t('dashboards_admin.form.refresh_hint')}
          >
            <MudSelect
              size="medium"
              value={String(form.refreshIntervalSeconds)}
              onMudChange={(e: CustomEvent<{ value: string }>) =>
                setForm({ ...form, refreshIntervalSeconds: Number(e.detail.value) || 300 })
              }
            >
              {REFRESH_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </MudSelect>
          </FormField>
        </FormRow>

        <FormRow columns={3}>
          <FormField
            label={t('dashboards_admin.form.default_range')}
            required
            hint={t('dashboards_admin.form.default_range_hint')}
          >
            <MudSelect
              size="medium"
              value={form.defaultRange}
              onMudChange={(e: CustomEvent<{ value: string }>) =>
                setForm({ ...form, defaultRange: e.detail.value })
              }
            >
              {DASHBOARD_DEFAULT_RANGES.map((value) => (
                <option key={value} value={value}>
                  {t(`dashboards_admin.default_range.${value}`)}
                </option>
              ))}
            </MudSelect>
          </FormField>
          <FormField
            label={t('dashboards_admin.form.max_range')}
            required
            hint={t('dashboards_admin.form.max_range_hint')}
            error={formSubmitted ? (rangeInvalid ?? undefined) : undefined}
          >
            <MudSelect
              size="medium"
              value={form.maxRange}
              onMudChange={(e: CustomEvent<{ value: string }>) =>
                setForm({ ...form, maxRange: e.detail.value })
              }
            >
              {DASHBOARD_RANGE_LIMITS.map((value) => (
                <option key={value} value={value}>
                  {t(`dashboards_admin.max_range.${value}`)}
                </option>
              ))}
            </MudSelect>
          </FormField>
          <FormField
            label={t('dashboards_admin.form.max_lookback')}
            required
            hint={t('dashboards_admin.form.max_lookback_hint')}
            error={formSubmitted ? (lookbackInvalid ?? undefined) : undefined}
          >
            <MudSelect
              size="medium"
              value={form.maxLookback}
              onMudChange={(e: CustomEvent<{ value: string }>) =>
                setForm({ ...form, maxLookback: e.detail.value })
              }
            >
              {DASHBOARD_RANGE_LIMITS.map((value) => (
                <option key={value} value={value}>
                  {t(`dashboards_admin.max_range.${value}`)}
                </option>
              ))}
            </MudSelect>
          </FormField>
        </FormRow>
      </FormSection>

      <FormSection>
        <fieldset className="simsm-dashboards-admin__fieldset">
          <legend>{t('dashboards_admin.form.translations')}</legend>
          <p className="simsm-dashboards-admin__form-hint">
            {t('dashboards_admin.form.translations_hint')}
          </p>
          {DASHBOARD_TRANSLATION_LOCALES.map((locale) => (
            <div key={locale} className="simsm-dashboards-admin__translation">
              <h3 className="simsm-dashboards-admin__translation-title" lang={locale}>
                {LOCALE_LABELS[locale]}
              </h3>
              <FormRow columns={3}>
                <FormField label={t('dashboards_admin.form.title')}>
                  <MudTextInput
                    size="md"
                    value={form.translations[locale].title}
                    placeholder={form.title}
                    onMudInput={(e: CustomEvent<{ value: string }>) =>
                      setTranslation(locale, { title: e.detail.value })
                    }
                  />
                </FormField>
                <FormField label={t('dashboards_admin.form.description')}>
                  <MudTextInput
                    size="md"
                    value={form.translations[locale].description}
                    placeholder={form.description}
                    onMudInput={(e: CustomEvent<{ value: string }>) =>
                      setTranslation(locale, { description: e.detail.value })
                    }
                  />
                </FormField>
                <FormField
                  label={t('dashboards_admin.form.translation.source')}
                  hint={t('dashboards_admin.form.translation.source_hint')}
                >
                  <MudSelect
                    size="medium"
                    value={form.translations[locale].grafanaUid}
                    onMudChange={(e: CustomEvent<{ value: string }>) =>
                      setTranslation(locale, { grafanaUid: e.detail.value })
                    }
                  >
                    {translationSourceOptions(form.translations[locale].grafanaUid).map(
                      (option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ),
                    )}
                  </MudSelect>
                </FormField>
              </FormRow>
            </div>
          ))}
        </fieldset>
      </FormSection>

      <FormSection>
        <fieldset className="simsm-dashboards-admin__fieldset">
          <legend>{t('dashboards_admin.form.roles')}</legend>
          <p className="simsm-dashboards-admin__form-hint">
            {t('dashboards_admin.form.roles_hint')}
          </p>
          <div className="simsm-dashboards-admin__roles">
            {observerRoles.map((roleCode) => (
              <MudCheckbox
                key={roleCode}
                size="md"
                checked={form.roleScope.has(roleCode)}
                onMudChange={(e: CustomEvent<{ checked: boolean }>) =>
                  toggleRole(roleCode, e.detail.checked)
                }
              >
                <span slot="label">{roleLabel(roleCode)}</span>
              </MudCheckbox>
            ))}
          </div>
          <div className="simsm-dashboards-admin__new-role">
            <MudTextInput
              size="md"
              value={newRoleCode}
              aria-label={t('dashboards_admin.form.new_role_code')}
              placeholder="OBSERVER_CNAS"
              onMudInput={(e: CustomEvent<{ value: string }>) =>
                setNewRoleCode(e.detail.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))
              }
            />
            <MudTextInput
              size="md"
              value={newRoleName}
              aria-label={t('dashboards_admin.form.new_role_name')}
              placeholder={t('dashboards_admin.form.new_role_name')}
              onMudInput={(e: CustomEvent<{ value: string }>) => setNewRoleName(e.detail.value)}
            />
            <MudButton
              shape="circular"
              variant="neutral"
              size="sm"
              disabled={!newRoleCodeReady || createRole.isPending}
              onClick={() => {
                setRefusal(null);
                createRole.mutate({
                  roleCode: newRoleCode.trim().toUpperCase(),
                  displayName: newRoleName.trim(),
                });
              }}
            >
              <MudIcon slot="icon-start" name="plus-large" size={16} />
              {t('dashboards_admin.form.add_role')}
            </MudButton>
          </div>
          <p className="simsm-dashboards-admin__form-hint">
            {t('dashboards_admin.form.new_role_hint')}
          </p>

          <div className="simsm-dashboards-admin__reach">
            <h3 className="simsm-dashboards-admin__reach-title">
              {t('dashboards_admin.reach.title')}
            </h3>
            {scope.length === 0 ? (
              <MudInfoBox emphasis="strong" variant="info">
                {t('dashboards_admin.reach.not_shared')}
              </MudInfoBox>
            ) : (
              <ul className="simsm-dashboards-admin__reach-list">
                {reach.map((row) => (
                  <li key={row.roleCode} className="simsm-dashboards-admin__reach-row">
                    <span>{roleLabel(row.roleCode)}</span>
                    <MudTag
                      variant="status"
                      size="sm"
                      semantic={row.kind === 'direct' ? 'brand' : 'accent'}
                      label={
                        row.kind === 'direct'
                          ? t('dashboards_admin.reach.direct')
                          : t('dashboards_admin.reach.inherited', {
                              role: roleLabel(row.via ?? ''),
                            })
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
            {scope.length > 0 && reach.some((row) => row.kind === 'inherited') ? (
              <p className="simsm-dashboards-admin__form-hint">
                {t('dashboards_admin.reach.inherited_hint')}
              </p>
            ) : null}
          </div>
        </fieldset>

        <fieldset className="simsm-dashboards-admin__fieldset">
          <legend>{t('dashboards_admin.form.visibility')}</legend>
          <MudSwitch
            checked={form.active}
            onMudChange={(e: CustomEvent<{ checked: boolean }>) =>
              setForm({ ...form, active: e.detail.checked })
            }
          >
            <span slot="label">{t('dashboards_admin.form.active')}</span>
          </MudSwitch>
          <p className="simsm-dashboards-admin__form-hint">
            {t('dashboards_admin.form.active_hint')}
          </p>
        </fieldset>
      </FormSection>
    </FormPage>
  );
}
